import React, { useState, useEffect } from 'react';
import api from '../../api';

const ACCENT = '#C9A84C';

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDate(d) {
  return d.toISOString().split('T')[0];
}

function formatDisplayDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function formatTime(t) {
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

export default function DateTimeSelect({ service, onSelect, onBack }) {
  const [selectedDate, setSelectedDate] = useState(formatDate(addDays(new Date(), 1)));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);

  const today = new Date();
  const startDay = addDays(today, weekOffset === 0 ? 0 : 1 + weekOffset * 7);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(startDay, i);
    return { date: formatDate(d), day: d.toLocaleDateString('en-US', { weekday: 'short' }), num: d.getDate() };
  });

  useEffect(() => {
    setLoading(true);
    setSlots([]);
    api.get(`/availability?date=${selectedDate}&service_id=${service.id}`)
      .then(r => setSlots(r.data))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [selectedDate, service.id]);

  const regularSlots = slots.filter(s => !s.is_after_hours);
  const afterHoursSlots = slots.filter(s => s.is_after_hours);
  const isSunday = new Date(selectedDate + 'T00:00:00').getDay() === 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '28px' }}>
        <button onClick={onBack} className="btn btn-ghost" style={{ padding: '8px 12px' }}>← Back</button>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Date & Time
          </h2>
          <p style={{ color: '#888', fontSize: '13px' }}>
            {service.name} · {service.duration_minutes} min · from ${parseFloat(service.price).toFixed(0)}
          </p>
        </div>
      </div>

      {/* Week nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <button
          onClick={() => setWeekOffset(w => Math.max(0, w - 1))}
          className="btn btn-ghost"
          disabled={weekOffset === 0}
          style={{ padding: '6px 12px', fontSize: '16px', minWidth: '36px' }}
        >←</button>
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px' }}>
          {weekDays.map(({ date, day, num }) => {
            const isSun = new Date(date + 'T00:00:00').getDay() === 0;
            const isSelected = selectedDate === date;
            return (
              <button key={date} onClick={() => setSelectedDate(date)} style={{
                padding: '10px 4px', borderRadius: '6px', border: '1px solid',
                borderColor: isSelected ? ACCENT : '#222',
                background: isSelected ? 'rgba(201,168,76,0.1)' : '#141414',
                color: isSelected ? ACCENT : isSun ? 'rgba(201,168,76,0.6)' : '#888',
                cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
              }}>
                <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{day}</div>
                <div style={{ fontSize: '15px', fontWeight: 800, marginTop: '2px' }}>{num}</div>
                {isSun && <div style={{ fontSize: '8px', color: ACCENT, marginTop: '2px', letterSpacing: '0.06em' }}>AH</div>}
              </button>
            );
          })}
        </div>
        <button onClick={() => setWeekOffset(w => w + 1)} className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: '16px', minWidth: '36px' }}>→</button>
      </div>

      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ color: '#888', fontSize: '14px' }}>{formatDisplayDate(selectedDate)}</span>
        {isSunday && (
          <span style={{ color: ACCENT, fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            After Hours Only
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : slots.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: '#888', padding: '40px' }}>
          No available slots for this day.
          <br />
          <span style={{ fontSize: '13px', color: '#555' }}>Try another date.</span>
        </div>
      ) : (
        <div>
          {regularSlots.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: '10px' }}>
                Regular Hours · ${parseFloat(regularSlots[0]?.price || service.price).toFixed(0)}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '8px' }}>
                {regularSlots.map(({ start_time, price }) => (
                  <button
                    key={start_time}
                    onClick={() => onSelect({ date: selectedDate, start_time, is_after_hours: false, price })}
                    className="service-card"
                    style={{
                      padding: '14px', borderRadius: '6px', border: '1px solid #222',
                      background: '#141414', color: '#fff', cursor: 'pointer', fontWeight: 700,
                      fontSize: '14px',
                    }}
                  >
                    {formatTime(start_time)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {afterHoursSlots.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: '10px' }}>
                After Hours · ${parseFloat(afterHoursSlots[0]?.price || service.after_hours_price || service.price).toFixed(0)}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '8px' }}>
                {afterHoursSlots.map(({ start_time, price }) => (
                  <button
                    key={start_time}
                    onClick={() => onSelect({ date: selectedDate, start_time, is_after_hours: true, price })}
                    style={{
                      padding: '14px', borderRadius: '6px',
                      border: '1px solid rgba(201,168,76,0.3)',
                      background: 'rgba(201,168,76,0.06)', color: ACCENT, cursor: 'pointer',
                      fontWeight: 700, fontSize: '14px', transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = ACCENT; e.currentTarget.style.background = 'rgba(201,168,76,0.14)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(201,168,76,0.3)'; e.currentTarget.style.background = 'rgba(201,168,76,0.06)'; }}
                  >
                    {formatTime(start_time)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {slots.length > 0 && (
        <p style={{ color: '#444', fontSize: '11px', marginTop: '18px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          AH = After Hours · Same-day booking requires 4+ hours notice
        </p>
      )}
    </div>
  );
}
